import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { requestsApi, usersApi } from '../lib/api';

const priorityOptions = [
  { value: 'P1', label: 'P1 (긴급) - 서비스 불가' },
  { value: 'P2', label: 'P2 (중요) - 기능 저하' },
  { value: 'P3', label: 'P3 (일반) - 개선/변경' },
];

const typeOptions = [
  { value: 'Bugfix', label: '버그수정' },
  { value: 'Enhancement', label: '개선' },
  { value: 'Feature', label: '신규기능' },
  { value: 'Support', label: '지원' },
  { value: 'Other', label: '기타' },
];

const channelOptions = [
  { value: 'Email', label: '이메일' },
  { value: 'Phone', label: '전화' },
  { value: 'Meeting', label: '미팅' },
  { value: 'Chat', label: '채팅' },
  { value: 'Other', label: '기타' },
];

interface FormData {
  date: string;
  client: string;
  channel: string;
  priority: string;
  type: string;
  title: string;
  description: string;
  doneCriteria: string;
  estimatedHours: string;
  assigneeId: string;
}

const initialFormData: FormData = {
  date: new Date().toISOString().split('T')[0],
  client: '',
  channel: 'Email',
  priority: 'P3',
  type: 'Bugfix',
  title: '',
  description: '',
  doneCriteria: '',
  estimatedHours: '',
  assigneeId: '',
};

export default function RequestFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  // 사용자 목록 로드
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });
  const users = usersData?.data || [];

  // 수정 모드: 기존 데이터 로드
  const { data: requestData, isLoading } = useQuery({
    queryKey: ['request', id],
    queryFn: () => requestsApi.get(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (requestData?.data) {
      const req = requestData.data;
      setFormData({
        date: req.date,
        client: req.client,
        channel: req.channel,
        priority: req.priority,
        type: req.type,
        title: req.title,
        description: req.description || '',
        doneCriteria: req.doneCriteria || '',
        estimatedHours: req.estimatedHours?.toString() || '',
        assigneeId: req.assigneeId?.toString() || '',
      });
    }
  }, [requestData]);

  // 생성
  const createMutation = useMutation({
    mutationFn: (data: any) => requestsApi.create(data),
    onSuccess: (response) => {
      navigate(`/requests/${response.data?.id || ''}`);
    },
  });

  // 수정
  const updateMutation = useMutation({
    mutationFn: (data: any) => requestsApi.update(id!, data),
    onSuccess: () => {
      navigate(`/requests/${id}`);
    },
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.date) newErrors.date = '날짜를 입력하세요';
    if (!formData.client.trim()) newErrors.client = '고객명을 입력하세요';
    if (!formData.title.trim()) newErrors.title = '제목을 입력하세요';

    if (formData.estimatedHours && isNaN(parseFloat(formData.estimatedHours))) {
      newErrors.estimatedHours = '유효한 숫자를 입력하세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const data = {
      date: formData.date,
      client: formData.client.trim(),
      channel: formData.channel,
      priority: formData.priority,
      type: formData.type,
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      doneCriteria: formData.doneCriteria.trim() || undefined,
      estimatedHours: formData.estimatedHours
        ? parseFloat(formData.estimatedHours)
        : undefined,
      assigneeId: formData.assigneeId ? parseInt(formData.assigneeId) : undefined,
    };

    mutation.mutate(data);
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩중...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? '요청 수정' : '새 요청'}
        </h1>
        <Link to={isEdit ? `/requests/${id}` : '/requests'} className="btn-ghost">
          취소
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* 기본 정보 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              요청일 *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`input ${errors.date ? 'input-error' : ''}`}
            />
            {errors.date && (
              <p className="text-sm text-red-600 mt-1">{errors.date}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              채널
            </label>
            <select
              name="channel"
              value={formData.channel}
              onChange={handleChange}
              className="input"
            >
              {channelOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            고객 *
          </label>
          <input
            type="text"
            name="client"
            value={formData.client}
            onChange={handleChange}
            className={`input ${errors.client ? 'input-error' : ''}`}
            placeholder="고객사 또는 팀명"
          />
          {errors.client && (
            <p className="text-sm text-red-600 mt-1">{errors.client}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              우선순위
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="input"
            >
              {priorityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              유형
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="input"
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            담당자
          </label>
          <select
            name="assigneeId"
            value={formData.assigneeId}
            onChange={handleChange}
            className="input"
          >
            <option value="">미배정</option>
            {users.map((user: any) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            제목 *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`input ${errors.title ? 'input-error' : ''}`}
            placeholder="요청 제목"
          />
          {errors.title && (
            <p className="text-sm text-red-600 mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            내용
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="input"
            rows={5}
            placeholder="요청 내용을 상세히 작성하세요"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            완료 기준
          </label>
          <textarea
            name="doneCriteria"
            value={formData.doneCriteria}
            onChange={handleChange}
            className="input"
            rows={3}
            placeholder="어떤 조건이 충족되면 완료로 볼 수 있는지 작성하세요"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            예상 소요시간 (시간)
          </label>
          <input
            type="number"
            name="estimatedHours"
            value={formData.estimatedHours}
            onChange={handleChange}
            step="0.5"
            min="0"
            className={`input w-32 ${errors.estimatedHours ? 'input-error' : ''}`}
            placeholder="예: 2.5"
          />
          {errors.estimatedHours && (
            <p className="text-sm text-red-600 mt-1">{errors.estimatedHours}</p>
          )}
          {formData.estimatedHours && parseFloat(formData.estimatedHours) > 6 && (
            <p className="text-sm text-yellow-600 mt-1">
              6시간 초과 시 별도 견적이 필요합니다
            </p>
          )}
        </div>

        {/* 에러 메시지 */}
        {mutation.error && (
          <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
            저장에 실패했습니다. 다시 시도해주세요.
          </div>
        )}

        {/* 제출 버튼 */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Link
            to={isEdit ? `/requests/${id}` : '/requests'}
            className="btn-secondary"
          >
            취소
          </Link>
          <button
            type="submit"
            className="btn-primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? '저장중...' : isEdit ? '수정' : '등록'}
          </button>
        </div>
      </form>
    </div>
  );
}
